import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FileText,
  ArrowLeft,
  TrendingUp,
  Clock,
  DollarSign,
  Award,
  BarChart3,
  Calendar,
  CheckCircle2,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Analytics({ user, logout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [fineCalculator, setFineCalculator] = useState(null);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    fetchAnalytics();
    fetchFineCalculator();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem("standari_token");
      const response = await axios.get(`${API}/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchFineCalculator = async () => {
    setCalculating(true);
    try {
      const token = localStorage.getItem("standari_token");
      const response = await axios.get(`${API}/tools/fine-calculator?violation_type=all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFineCalculator(response.data);
    } catch (error) {
      console.error("Fine calculator not available");
    } finally {
      setCalculating(false);
    }
  };

  // Calculate ROI metrics
  const calculateROI = () => {
    if (!analytics) return null;

    const docsProcessed = analytics.processed_documents || 0;
    const hoursSavedPerDoc = 8; // Estimated hours saved per document
    const avgHourlyRate = 75; // Average technical writer hourly rate
    const totalTimeSaved = docsProcessed * hoursSavedPerDoc;
    const costSavings = totalTimeSaved * avgHourlyRate;
    const subscriptionCost = 49; // Pro tier monthly cost
    const roi = subscriptionCost > 0 ? ((costSavings - subscriptionCost) / subscriptionCost * 100).toFixed(0) : 0;

    return {
      timeSaved: totalTimeSaved,
      costSavings,
      roi,
      avgTimePerDoc: hoursSavedPerDoc
    };
  };

  const roiData = calculateROI();

  // Standards usage breakdown
  const getStandardsUsage = () => {
    // In real implementation, this would come from backend
    return [
      { name: "DITA", count: Math.floor((analytics?.processed_documents || 0) * 0.35), color: "bg-blue-500" },
      { name: "S1000D", count: Math.floor((analytics?.processed_documents || 0) * 0.25), color: "bg-green-500" },
      { name: "ISO 26514", count: Math.floor((analytics?.processed_documents || 0) * 0.15), color: "bg-yellow-500" },
      { name: "ASD-STE100", count: Math.floor((analytics?.processed_documents || 0) * 0.15), color: "bg-purple-500" },
      { name: "Other", count: Math.floor((analytics?.processed_documents || 0) * 0.10), color: "bg-slate-400" }
    ];
  };

  const standardsUsage = getStandardsUsage();
  const totalStandards = standardsUsage.reduce((sum, s) => sum + s.count, 0);

  return (
    <div className="min-h-screen gradient-bg" data-testid="analytics-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center space-x-2">
                <BarChart3 className="h-8 w-8 text-blue-600" />
                <span className="text-2xl font-heading font-bold gradient-text">Analytics & ROI</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-slate-600">Loading analytics...</p>
          </div>
        ) : (
          <>
            {/* Top Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="doc-card" data-testid="metric-documents">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium text-slate-600">Documents Processed</CardTitle>
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold font-heading text-slate-900">
                    {analytics?.processed_documents || 0}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {analytics?.total_documents || 0} total uploaded
                  </p>
                </CardContent>
              </Card>

              <Card className="doc-card" data-testid="metric-time-saved">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium text-slate-600">Time Saved</CardTitle>
                    <Clock className="h-5 w-5 text-green-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold font-heading text-slate-900">
                    {roiData?.timeSaved || 0} hrs
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    ≈ {Math.floor((roiData?.timeSaved || 0) / 40)} work weeks
                  </p>
                </CardContent>
              </Card>

              <Card className="doc-card" data-testid="metric-cost-savings">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium text-slate-600">Cost Savings</CardTitle>
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold font-heading text-green-600">
                    ${(roiData?.costSavings || 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    vs. traditional methods
                  </p>
                </CardContent>
              </Card>

              <Card className="doc-card" data-testid="metric-roi">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium text-slate-600">ROI</CardTitle>
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold font-heading text-blue-600">
                    {roiData?.roi || 0}%
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Return on investment
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Standards Usage & Fine Calculator */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Standards Usage */}
              <Card className="doc-card">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center">
                    <Award className="h-5 w-5 mr-2 text-blue-600" />
                    Standards Usage Breakdown
                  </CardTitle>
                  <CardDescription>Most frequently used compliance standards</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {standardsUsage.map((standard) => (
                    <div key={standard.name} className="space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span className="font-medium text-slate-700">{standard.name}</span>
                        <span className="text-slate-600">
                          {standard.count} docs ({totalStandards > 0 ? ((standard.count / totalStandards) * 100).toFixed(0) : 0}%)
                        </span>
                      </div>
                      <Progress 
                        value={totalStandards > 0 ? (standard.count / totalStandards) * 100 : 0} 
                        className="h-2"
                      />
                    </div>
                  ))}
                  {totalStandards === 0 && (
                    <p className="text-center text-slate-500 py-4">
                      Process documents to see standards usage
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Fine Calculator (Pro Tier Feature) */}
              <Card className="doc-card border-2 border-blue-200 bg-blue-50/30">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading flex items-center">
                      <AlertTriangle className="h-5 w-5 mr-2 text-yellow-600" />
                      Compliance Fine Calculator
                    </CardTitle>
                    <span className="text-xs font-semibold px-2 py-1 bg-blue-600 text-white rounded">PRO</span>
                  </div>
                  <CardDescription>Estimate potential regulatory fines and savings</CardDescription>
                </CardHeader>
                <CardContent>
                  {calculating ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <p className="mt-2 text-sm text-slate-600">Calculating...</p>
                    </div>
                  ) : fineCalculator ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-4 border border-slate-200">
                          <p className="text-xs font-medium text-slate-600 mb-1">Potential Risk</p>
                          <p className="text-2xl font-bold text-red-600">
                            ${(fineCalculator.total_risk || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-slate-200">
                          <p className="text-xs font-medium text-slate-600 mb-1">Savings with Compliance</p>
                          <p className="text-2xl font-bold text-green-600">
                            ${(fineCalculator.potential_savings || 50000).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 border border-slate-200 space-y-2">
                        <p className="text-sm font-semibold text-slate-900">Top Violation Risks:</p>
                        {fineCalculator.breakdown && Object.entries(fineCalculator.breakdown).slice(0, 3).map(([key, value]) => (
                          <div key={key} className="flex justify-between text-sm">
                            <span className="text-slate-600 capitalize">{key.replace('_', ' ')}:</span>
                            <span className="font-medium text-slate-900">${(value.fine || 0).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>

                      <p className="text-xs text-slate-500 italic">
                        💡 Maintaining compliant documentation reduces exposure to OSHA, IRS, and BOI penalties by up to 90%.
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-3" />
                      <p className="text-sm text-slate-600 mb-4">
                        Calculate your potential compliance savings
                      </p>
                      <Button onClick={fetchFineCalculator} className="btn-primary" size="sm">
                        Calculate Now
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Productivity Insights */}
            <Card className="doc-card mb-8">
              <CardHeader>
                <CardTitle className="font-heading flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-blue-600" />
                  Productivity Insights
                </CardTitle>
                <CardDescription>How Standari is accelerating your workflow</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-blue-50 rounded-lg p-5">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="p-2 bg-blue-600 rounded-lg">
                        <CheckCircle2 className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">{roiData?.avgTimePerDoc}x</p>
                        <p className="text-sm text-slate-600">Faster Processing</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-600">
                      Average time reduced from 40 hours to 5 hours per document
                    </p>
                  </div>

                  <div className="bg-green-50 rounded-lg p-5">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="p-2 bg-green-600 rounded-lg">
                        <TrendingUp className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">95%</p>
                        <p className="text-sm text-slate-600">Error Reduction</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-600">
                      Automated compliance checking reduces manual review errors
                    </p>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-5">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="p-2 bg-purple-600 rounded-lg">
                        <DollarSign className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">
                          ${analytics?.usage_credits || 100}
                        </p>
                        <p className="text-sm text-slate-600">Credits Remaining</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-600">
                      Usage credits for processing and exports
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="doc-card">
              <CardHeader>
                <CardTitle className="font-heading">Recent Activity</CardTitle>
                <CardDescription>Your latest document processing activity</CardDescription>
              </CardHeader>
              <CardContent>
                {analytics?.recent_documents && analytics.recent_documents.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.recent_documents.map((doc) => (
                      <div 
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <FileText className="h-5 w-5 text-blue-600" />
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{doc.filename}</p>
                            <p className="text-xs text-slate-500">
                              {new Date(doc.upload_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <span className={`text-xs font-medium px-2 py-1 rounded ${
                          doc.status === 'completed' ? 'bg-green-100 text-green-700' : 
                          doc.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-slate-200 text-slate-700'
                        }`}>
                          {doc.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">
                    No recent activity. Start processing documents to see your activity here.
                  </p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
